import { useState } from 'react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Finding } from '@/api/domains/analyses/types';
import styles from './AIAnalysis.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface AIAnalysisProps {
  finding: Finding;
}

export const AIAnalysis = ({ finding }: AIAnalysisProps) => {
  const { rag_insight } = finding;
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | number[]>(0);

  if (!rag_insight) {
    return null;
  }

  const isFallback =
    rag_insight.llm_confidence === 0.5 &&
    rag_insight.reasoning === 'Analysis could not be completed';

  const handleCopy = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isFallback) {
    return (
      <div className={styles.reasoningCard}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <i className="pi pi-sparkles" style={{ fontSize: '1.3rem' }} />
            <h3 className={styles.title}>AI Analysis</h3>
          </div>
        </div>

        <Divider className={styles.divider} />

        <div className={styles.warningBlock}>
          <i className="pi pi-info-circle" />
          <div>
            <strong>Manual Review Recommended</strong>
            <p>
              This finding requires careful examination. Review the comparison
              view and validation panel for detailed analysis.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reasoningCard}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <i className="pi pi-sparkles" style={{ fontSize: '1.3rem' }} />
          <h3 className={styles.title}>AI Analysis</h3>
        </div>
      </div>

      <Divider className={styles.divider} />

      <Accordion
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        className={styles.accordion}
      >
        <AccordionTab
          header={
            <div className={styles.accordionHeader}>
              <i className="pi pi-brain" />
              <span>Reasoning</span>
            </div>
          }
          className={styles.accordionTab}
        >
          <div className={styles.accordionContent}>
            <Button
              icon={
                copiedSection === 'reasoning' ? 'pi pi-check' : 'pi pi-copy'
              }
              text
              rounded
              size="small"
              className={styles.copyButton}
              onClick={() => handleCopy(rag_insight.reasoning, 'reasoning')}
              tooltip="Copy reasoning"
              tooltipOptions={{
                position: 'top',
                style: { color: 'white', fontSize: '10px' },
                showDelay: 300
              }}
            />
            <div className={styles.reasoningText}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {rag_insight.reasoning}
              </ReactMarkdown>
            </div>
          </div>
        </AccordionTab>

        {rag_insight.recommendations.length > 0 && (
          <AccordionTab
            header={
              <div className={styles.accordionHeader}>
                <i className="pi pi-lightbulb" />
                <span>Recommendations</span>
                <span className={styles.badge}>
                  {rag_insight.recommendations.length}
                </span>
              </div>
            }
            className={styles.accordionTab}
          >
            <ul className={styles.list}>
              {rag_insight.recommendations.map((rec, idx) => (
                <li key={idx} className={styles.listItem}>
                  <i className="pi pi-angle-right" />
                  <div className={styles.listItemText}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {rec}
                    </ReactMarkdown>
                  </div>
                </li>
              ))}
            </ul>
          </AccordionTab>
        )}

        {rag_insight.key_differences.length > 0 && (
          <AccordionTab
            header={
              <div className={styles.accordionHeader}>
                <i className="pi pi-arrows-h" />
                <span>Key Differences</span>
                <span className={styles.badge}>
                  {rag_insight.key_differences.length}
                </span>
              </div>
            }
            className={styles.accordionTab}
          >
            <ul className={styles.list}>
              {rag_insight.key_differences.map((diff, idx) => (
                <li key={idx} className={styles.listItem}>
                  <i className="pi pi-angle-right" />
                  <div className={styles.listItemText}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {diff}
                    </ReactMarkdown>
                  </div>
                </li>
              ))}
            </ul>
          </AccordionTab>
        )}

        {rag_insight.key_similarities &&
          rag_insight.key_similarities.length > 0 && (
            <AccordionTab
              header={
                <div className={styles.accordionHeader}>
                  <i className="pi pi-check-circle" />
                  <span>Key Similarities</span>
                  <span className={styles.badge}>
                    {rag_insight.key_similarities.length}
                  </span>
                </div>
              }
              className={styles.accordionTab}
            >
              <ul className={styles.list}>
                {rag_insight.key_similarities.map((sim, idx) => (
                  <li key={idx} className={styles.listItem}>
                    <i className="pi pi-angle-right" />
                    <div className={styles.listItemText}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {sim}
                      </ReactMarkdown>
                    </div>
                  </li>
                ))}
              </ul>
            </AccordionTab>
          )}
      </Accordion>

      {rag_insight.requires_manual_review && (
        <div className={styles.warningBlock}>
          <i className="pi pi-exclamation-triangle" />
          <div>
            <strong>Manual Review Required</strong>
            <p>
              This finding has been flagged for manual verification by a
              compliance officer.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
